import type { Language } from '@eformulieren/engine'
import { NormalizedEmail, type Side, normalizeEmail } from '@eformulieren/model'
import { type FormEvent, type ReactNode, type RefObject, useEffect, useRef, useState } from 'react'
import { SectionPicker, defaultPermission } from './SectionPicker.js'
import { type Scope, SectionScope, shownSectionCount } from './SectionScope.js'
import {
  type AdviceOffer,
  type InvitationOutcome,
  type InviteRequest,
  type ShareAnchor,
  type ShareNotice,
  type ShareRole,
  type ShareSection,
  type ShareStart,
  allSectionsNotes,
  allSectionsRights,
  inviteSummary,
  roleWord,
  sectionLabel,
} from './shareModel.js'
import type { Bilingual } from '../language/text.js'

/**
 * Het tabblad "Iemand uitnodigen" (besluit 1 van 30 oktober 2026, R-64, R-64a, R-65, R-65a, R-65b,
 * R-65c, R-140, R-184, mockups 01 en 02 van delen, 02 en 03 van advies).
 *
 * **Eerst de rol, dan wat erbij hoort.** "Wat mag deze persoon?" staat bovenaan, met Meelezen en
 * Meeschrijven en aan de interne kant Advies geven, elk met een regel uitleg. Bij meelezen en
 * meeschrijven volgen het adres en de secties; bij advies de vraag met haar onderwerp, het adres met
 * Binnen of Buiten NWO, wat u deelt en de termijn. De rol staat altijd op dezelfde plek, zodat een
 * andere keuze de rest niet onder de cursor wegschuift; in mockup 01 stond het adres boven de rol.
 *
 * **Geopend vanuit een vraag** (de tekstballon of het adviespaneel) is de rol ingeklapt tot één regel
 * met "Recht wijzigen", en krijgt de vraag de focus (A1). Verplicht zijn dan alleen de vraag en het
 * adres. Wat er gedeeld wordt is voorgekozen: de sectie van het onderwerp, of bij een vraag over het
 * hele formulier elke sectie die gedeeld kan worden, want de pod wil minstens één sectie.
 *
 * **Een fout hoort bij haar veld** (R-140): de melding staat eronder, het veld krijgt `aria-invalid`
 * en de melding in zijn beschrijving, en de focus gaat erheen. Een weigering van de pod met secties
 * hoort bij die secties.
 *
 * **Eerst de hele reikwijdte, dan pas de lijst** (opdracht van de productowner van 2 november
 * 2026, ontwerp 6.4). Onder "Welke secties?" staan twee keuzes: Alle secties (de standaard) en Alleen
 * bepaalde secties. Alle secties geeft elke sectie die nu in het formulier staat, met schrijven waar
 * de deler dat kan geven en anders lezen (`allSectionsRights`); welke secties dan alleen te lezen
 * zijn, en waarom, staat er per reden direct onder (besluit 1). Pas Alleen bepaalde secties toont de
 * lijst met vinkjes, leeg, met Alles kiezen. Geopend met voorgekozen secties ("Deze sectie delen")
 * staat de keuze op bepaalde secties. Een weigering van de pod die secties noemt zet de keuze op
 * bepaalde secties met alles wat gekozen was aangevinkt, zodat de melding bij die secties kan staan
 * (R-140).
 *
 * Versturen gebeurt met de knop in de voet van de dialoog (`form`), die op dit formulier wijst.
 */

export interface ShareInviteProps {
  readonly taal: Language
  readonly formId: string
  readonly kant: Side
  readonly secties: readonly ShareSection[]
  readonly advies?: AdviceOffer
  readonly start?: ShareStart
  readonly busy: boolean
  readonly onBusy: (busy: boolean) => void
  readonly onInvite: (request: InviteRequest) => Promise<InvitationOutcome>
  /** Na versturen: voor de melding en de nieuwe regel in de lijst. */
  readonly onSent: (address: string, id: string | undefined) => void
  /** Het veld dat bij openen de focus krijgt. */
  readonly firstField: RefObject<HTMLElement | null>
}

const T = {
  rol: { nl: 'Wat mag deze persoon?', en: 'What may this person do?' },
  recht: { nl: 'Recht', en: 'Access' },
  rechtWijzigen: { nl: 'Recht wijzigen', en: 'Change access' },
  email: { nl: 'E-mailadres', en: 'Email address' },
  // Geen lijst met namen (R-64a): het adres waarmee de ander inlogt.
  emailUitleg: { nl: 'Het adres waarmee deze persoon inlogt.', en: 'The address this person signs in with.' },
  secties: { nl: 'Welke secties?', en: 'Which sections?' },
  kies: { nl: 'Kies de secties', en: 'Choose the sections' },
  vraag: { nl: 'Uw vraag', en: 'Your question' },
  over: { nl: 'Over {a}', en: 'About {a}' },
  hetFormulier: { nl: 'het formulier', en: 'the form' },
  sectie: { nl: 'sectie {s}', en: 'section {s}' },
  anderOnderwerp: { nl: 'ander onderwerp kiezen', en: 'choose another subject' },
  onderwerp: { nl: 'Onderwerp', en: 'Subject' },
  waar: { nl: 'Waar werkt de adviseur?', en: 'Where does the adviser work?' },
  binnen: { nl: 'Binnen NWO', en: 'Within NWO' },
  buiten: { nl: 'Buiten NWO', en: 'Outside NWO' },
  binnenUitleg: { nl: 'Binnen NWO: alleen te openen met een NWO-account.', en: 'Within NWO: can only be opened with an NWO account.' },
  buitenUitleg: { nl: 'Buiten NWO: de adviseur logt in zonder NWO-account.', en: 'Outside NWO: the adviser signs in without an NWO account.' },
  deelt: { nl: 'Wat u deelt: {n}', en: 'What you share: {n}' },
  eenSectie: { nl: '1 sectie', en: '1 section' },
  nSecties: { nl: '{n} secties', en: '{n} sections' },
  welkeDelen: { nl: 'Secties om te lezen', en: 'Sections to read' },
  termijn: { nl: 'Antwoord graag voor', en: 'Answer preferably before' },
  nietVerplicht: { nl: '(niet verplicht)', en: '(optional)' },
  ongeldigAdres: { nl: 'Vul een geldig e-mailadres in, zoals naam@voorbeeld.nl.', en: 'Enter a valid email address, such as name@example.org.' },
  geenSectie: { nl: 'Kies minstens één sectie.', en: 'Choose at least one section.' },
  geenVraag: { nl: 'Schrijf uw vraag aan de adviseur.', en: 'Write your question for the adviser.' },
} as const satisfies Readonly<Record<string, Bilingual>>

const EXPLANATION: Readonly<Record<ShareRole, Bilingual>> = {
  meelezen: { nl: 'Leest mee. Wijzigt niets.', en: 'Reads along. Changes nothing.' },
  meeschrijven: { nl: 'Schrijft mee en plaatst opmerkingen. Dient niet in.', en: 'Writes and adds comments. Does not submit.' },
  advies: {
    nl: 'Leest de secties die u kiest en beantwoordt uw vraag. Wijzigt niets. De aanvrager ziet dit niet.',
    en: 'Reads the sections you choose and answers your question. Changes nothing. The applicant does not see this.',
  },
}

const CANNOT_WRITE: Readonly<Record<'toetser' | 'geen', Bilingual>> = {
  toetser: { nl: 'Kan nu niet: u toetst dit formulier, en een toetser geeft geen schrijfrecht.', en: 'Not possible now: you review this form, and a reviewer does not give write access.' },
  geen: { nl: 'Kan nu niet: u schrijft zelf in geen van deze secties.', en: 'Not possible now: you do not write in any of these sections yourself.' },
}

/** Een fout bij het formulier: van de pagina zelf vóór verzenden, of van de pod daarna. */
interface FormError {
  readonly tekst: string
  readonly secties: readonly string[]
  readonly veld?: 'email' | 'vraag' | 'adviseurKant' | 'secties'
}

type Rights = Readonly<Record<string, 'lezen' | 'bewerken'>>

/** Wat er bij advies standaard gedeeld wordt: de sectie van het onderwerp, of bij het formulier elke deelbare. */
function adviceDefault(anchor: ShareAnchor, sections: readonly ShareSection[]): Rights {
  const open = sections.filter((section) => section.adviesUitgesloten !== true)
  if (anchor.soort === 'formulier') return Object.fromEntries(open.filter((section) => section.verborgen !== true).map((section) => [section.id, 'lezen']))
  return open.some((section) => section.id === anchor.sectieId) ? { [anchor.sectieId]: 'lezen' } : {}
}

function anchorValue(anchor: ShareAnchor): string {
  return anchor.soort === 'formulier' ? 'formulier' : anchor.soort === 'sectie' ? `sectie:${anchor.sectieId}` : 'veld'
}

export function ShareInvite({
  taal: language,
  formId,
  kant: side,
  secties: sections,
  advies: adviceOffer,
  start,
  busy,
  onBusy,
  onInvite,
  onSent,
  firstField,
}: ShareInviteProps): ReactNode {
  const canWrite = sections.some((section) => section.magBewerken)
  const roles: readonly ShareRole[] = side === 'intern' && adviceOffer !== undefined ? ['meelezen', 'meeschrijven', 'advies'] : ['meelezen', 'meeschrijven']
  const startRole: ShareRole =
    start?.rol === 'advies' && adviceOffer !== undefined ? 'advies' : start?.rol === 'meeschrijven' && canWrite ? 'meeschrijven' : 'meelezen'
  const startAnchor: ShareAnchor = start?.anker ?? { soort: 'formulier' }
  const [role, setRole] = useState<ShareRole>(startRole)
  const [collapsed, setCollapsed] = useState(startRole === 'advies' && start?.rol === 'advies')
  const [email, setEmail] = useState('')
  const [rights, setRights] = useState<Rights>(() =>
    Object.fromEntries(
      (start?.secties ?? []).flatMap((id) => {
        const section = sections.find((candidate) => candidate.id === id)
        return section === undefined ? [] : [[id, defaultPermission(section, startRole === 'meeschrijven' ? 'schrijven' : 'lezen')]]
      }),
    ),
  )
  const shownCount = shownSectionCount(sections)
  const [scope, setScope] = useState<Scope>((start?.secties?.length ?? 0) > 0 || shownCount === 0 ? 'bepaald' : 'alle')
  /** Welk vinkje de focus krijgt zodra de lijst er staat, na een weigering die secties noemt. */
  const [focusSection, setFocusSection] = useState<string | undefined>(undefined)
  const [question, setQuestion] = useState('')
  const [anchor, setAnchor] = useState<ShareAnchor>(startAnchor)
  const [choosingAnchor, setChoosingAnchor] = useState(false)
  const [adviserSide, setAdviserSide] = useState<Side>('intern')
  const [shared, setShared] = useState<Rights>(() => {
    const preset = start?.secties?.filter((id) => sections.some((section) => section.id === id && section.adviesUitgesloten !== true)) ?? []
    return preset.length > 0 ? Object.fromEntries(preset.map((id) => [id, 'lezen'])) : adviceDefault(startAnchor, sections)
  })
  const [sharedOpen, setSharedOpen] = useState(false)
  const [deadline, setDeadline] = useState('')
  const [error, setError] = useState<FormError | undefined>(undefined)
  const emailField = useRef<HTMLInputElement>(null)
  const questionField = useRef<HTMLTextAreaElement>(null)
  const sectionsRef = useRef<HTMLDivElement>(null)
  const insideRef = useRef<HTMLInputElement>(null)
  const ids = {
    rol: `${formId}-rol`,
    email: `${formId}-email`,
    emailUitleg: `${formId}-email-uitleg`,
    vraag: `${formId}-vraag`,
    anker: `${formId}-anker`,
    onderwerp: `${formId}-onderwerp`,
    kant: `${formId}-kant`,
    termijn: `${formId}-termijn`,
    fout: `${formId}-fout`,
    secties: `${formId}-secties`,
  }

  useEffect(() => {
    if (focusSection === undefined) return
    const boxes = Array.from(sectionsRef.current?.querySelectorAll<HTMLInputElement>('input[type=checkbox]:not([disabled])') ?? [])
    ;(boxes.find((box) => box.id.endsWith(`-${focusSection}`)) ?? boxes[0])?.focus()
    setFocusSection(undefined)
  }, [focusSection])

  // Welk veld de dialoog bij openen de focus geeft: de vraag bij advies (A1), anders het adres.
  useEffect(() => {
    ;(firstField as { current: HTMLElement | null }).current = role === 'advies' ? questionField.current : emailField.current
  })

  function chooseRole(next: ShareRole): void {
    setRole(next)
    setError(undefined)
    if (next === 'meelezen') setRights((previous) => Object.fromEntries(Object.keys(previous).map((id) => [id, 'lezen'])))
    if (next === 'meeschrijven') {
      setRights((previous) =>
        Object.fromEntries(
          Object.keys(previous).map((id) => {
            const section = sections.find((candidate) => candidate.id === id)
            return [id, section === undefined ? 'lezen' : defaultPermission(section, 'schrijven')]
          }),
        ),
      )
    }
  }

  function chooseAnchor(value: string): void {
    const next: ShareAnchor =
      value === 'formulier' ? { soort: 'formulier' } : value === 'veld' && startAnchor.soort === 'veld' ? startAnchor : { soort: 'sectie', sectieId: value.slice('sectie:'.length) }
    setAnchor(next)
    if (next.soort !== 'formulier') {
      const section = sections.find((candidate) => candidate.id === next.sectieId)
      if (section !== undefined && section.adviesUitgesloten !== true) setShared((previous) => ({ ...previous, [next.sectieId]: 'lezen' }))
    } else if (Object.keys(shared).length === 0) {
      setShared(adviceDefault(next, sections))
    }
  }

  function fail(found: FormError): void {
    setError(found)
    if (found.veld === 'email') emailField.current?.focus()
    else if (found.veld === 'vraag') questionField.current?.focus()
    else if (found.veld === 'adviseurKant') insideRef.current?.focus()
    else if (found.veld === 'secties') {
      if (role === 'advies') setSharedOpen(true)
      if (role !== 'advies' && scope === 'alle') {
        // Bij Alle secties staat er geen vinkje waar de melding aan kan hangen: open de lijst met wat
        // er gekozen was, en zet de focus op de eerste sectie die de pod noemde (R-140).
        setRights(allSectionsRights(sections, role))
        setScope('bepaald')
      }
      setFocusSection(found.secties[0] ?? '')
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    if (busy) return
    const address = normalizeEmail(email)
    if (role === 'advies' && question.trim() === '') return fail({ tekst: T.geenVraag[language], secties: [], veld: 'vraag' })
    if (!NormalizedEmail.safeParse(address).success) return fail({ tekst: T.ongeldigAdres[language], secties: [], veld: 'email' })
    const given = role !== 'advies' && scope === 'alle' ? allSectionsRights(sections, role) : rights
    const chosen = role === 'advies' ? shared : given
    if (Object.keys(chosen).length === 0) return fail({ tekst: T.geenSectie[language], secties: [], veld: 'secties' })
    const request: InviteRequest =
      role === 'advies'
        ? {
            soort: 'advies',
            email: address,
            vraag: question.trim(),
            anker: anchor,
            adviseurKant: adviceOffer?.buitenNwo === true ? adviserSide : 'intern',
            gedeeld: Object.keys(shared),
            termijn: deadline === '' ? null : deadline,
          }
        : { soort: 'deling', email: address, rechten: given }
    setError(undefined)
    onBusy(true)
    try {
      const outcome = await onInvite(request)
      if (outcome.soort === 'verstuurd') {
        onSent(address, outcome.id)
        return
      }
      const notice: ShareNotice = outcome.melding
      const field = notice.veld ?? (notice.secties.length > 0 ? 'secties' : undefined)
      fail({ tekst: notice.tekst, secties: notice.secties, ...(field === undefined ? {} : { veld: field }) })
    } finally {
      onBusy(false)
    }
  }

  const fieldError = (field: FormError['veld']) => (error !== undefined && error.veld === field ? error : undefined)
  const invalid = (field: FormError['veld'], described?: string) =>
    fieldError(field) === undefined
      ? described === undefined
        ? {}
        : { 'aria-describedby': described }
      : { 'aria-invalid': true, 'aria-describedby': [described, ids.fout].filter((part) => part !== undefined).join(' ') }
  const alertFor = (field: FormError['veld']) =>
    fieldError(field) === undefined ? null : (
      <p id={ids.fout} role="alert" className="ef-veld__melding--hard">
        {error?.tekst}
      </p>
    )
  const sectionError = fieldError('secties')
  const pickerError = sectionError === undefined ? undefined : { id: ids.fout, tekst: sectionError.tekst, secties: sectionError.secties }
  const sharedCount = Object.keys(shared).length
  const allNotes = role === 'advies' ? [] : allSectionsNotes(sections, role, language)
  const anchorText =
    anchor.soort === 'formulier'
      ? T.hetFormulier[language]
      : anchor.soort === 'veld'
        ? anchor.label
        : T.sectie[language].replace(
            '{s}',
            (() => {
              const section = sections.find((candidate) => candidate.id === anchor.sectieId)
              return section === undefined ? '' : sectionLabel(section)
            })(),
          )
  const summary = inviteSummary(
    role === 'advies'
      ? { rol: 'advies', email, gedeeld: Object.keys(shared) }
      : scope === 'alle'
        ? { rol: role, email, rechten: allSectionsRights(sections, role), alle: true }
        : { rol: role, email, rechten: rights },
    sections,
    language,
  )

  const emailBlock = (
    <div className="ef-veld" data-status={fieldError('email') === undefined ? undefined : 'fout'}>
      <label htmlFor={ids.email} className="ef-veld__label">
        <span className="ef-veld__labeltekst">{T.email[language]}</span>
      </label>
      <p id={ids.emailUitleg} className="ef-veld__hulptekst">
        {T.emailUitleg[language]}
      </p>
      <input
        ref={emailField}
        id={ids.email}
        type="email"
        autoComplete="email"
        className="ef-invoer"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value)
        }}
        {...invalid('email', ids.emailUitleg)}
      />
      {alertFor('email')}
    </div>
  )

  return (
    <form id={formId} className="ef-deeldialoog__formulier" noValidate onSubmit={(event) => void handleSubmit(event)}>
      <fieldset className="ef-deeldialoog__velden" disabled={busy}>
        {collapsed ? (
          <div className="ef-deeldialoog__rolregel">
            <p className="ef-deeldialoog__rolkort">
              <span className="ef-veld__hulptekst">{T.recht[language]}</span> <strong>{roleWord('advies', language)}</strong>{' '}
              <span>{EXPLANATION.advies[language]}</span>
            </p>
            <button
              type="button"
              className="ef-tekstknop"
              onClick={() => {
                setCollapsed(false)
              }}
            >
              {T.rechtWijzigen[language]}
            </button>
          </div>
        ) : (
          <div className="ef-veld">
            <p id={ids.rol} className="ef-veld__label">
              <span className="ef-veld__labeltekst">{T.rol[language]}</span>
            </p>
            <div role="radiogroup" aria-labelledby={ids.rol} className="ef-rolkeuze">
              {roles.map((option) => {
                const disabled = option === 'meeschrijven' && !canWrite
                const explanationId = `${formId}-rol-${option}`
                const nameId = `${explanationId}-naam`
                const reason = disabled
                  ? CANNOT_WRITE[sections.length > 0 && sections.every((section) => section.schrijfReden === 'toetser') ? 'toetser' : 'geen'][language]
                  : EXPLANATION[option][language]
                return (
                  <label key={option} className="ef-rolkeuze__optie" data-gekozen={role === option ? 'ja' : undefined} data-uit={disabled ? 'ja' : undefined}>
                    <input
                      type="radio"
                      name={`${formId}-rol`}
                      value={option}
                      checked={role === option}
                      disabled={disabled}
                      aria-labelledby={nameId}
                      aria-describedby={explanationId}
                      onChange={() => {
                        chooseRole(option)
                      }}
                    />
                    <span className="ef-rolkeuze__tekst">
                      <span id={nameId} className="ef-rolkeuze__naam">
                        {roleWord(option, language)}
                      </span>
                      <span id={explanationId} className="ef-rolkeuze__uitleg">
                        {reason}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {role === 'advies' ? (
          <>
            <div className="ef-veld" data-status={fieldError('vraag') === undefined ? undefined : 'fout'}>
              <label htmlFor={ids.vraag} className="ef-veld__label">
                <span className="ef-veld__labeltekst">{T.vraag[language]}</span>
              </label>
              <p id={ids.anker} className="ef-veld__hulptekst">
                {T.over[language].replace('{a}', anchorText)}
                {choosingAnchor ? null : (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="ef-tekstknop"
                      onClick={() => {
                        setChoosingAnchor(true)
                      }}
                    >
                      {T.anderOnderwerp[language]}
                    </button>
                  </>
                )}
              </p>
              {choosingAnchor ? (
                <div className="ef-veld">
                  <label htmlFor={ids.onderwerp} className="ef-veld__label">
                    <span className="ef-veld__labeltekst">{T.onderwerp[language]}</span>
                  </label>
                  <select id={ids.onderwerp} className="ef-invoer" value={anchorValue(anchor)} onChange={(event) => chooseAnchor(event.target.value)}>
                    <option value="formulier">{T.hetFormulier[language]}</option>
                    {startAnchor.soort === 'veld' ? <option value="veld">{startAnchor.label}</option> : null}
                    {sections
                      .filter((section) => section.adviesUitgesloten !== true)
                      .map((section) => (
                        <option key={section.id} value={`sectie:${section.id}`}>
                          {T.sectie[language].replace('{s}', sectionLabel(section))}
                        </option>
                      ))}
                  </select>
                </div>
              ) : null}
              <textarea
                ref={questionField}
                id={ids.vraag}
                className="ef-invoer ef-deeldialoog__vraag"
                rows={3}
                value={question}
                onChange={(event) => {
                  setQuestion(event.target.value)
                }}
                {...invalid('vraag', ids.anker)}
              />
              {alertFor('vraag')}
            </div>
            {emailBlock}
            <fieldset className="ef-keuzegroep ef-deeldialoog__kant" {...invalid('adviseurKant', ids.kant)}>
              <legend className="ef-veld__label">
                <span className="ef-veld__labeltekst">{T.waar[language]}</span>
              </legend>
              <div className="ef-deeldialoog__kantkeuze">
                <label className="ef-keuze">
                  <input
                    ref={insideRef}
                    type="radio"
                    name={`${formId}-kant`}
                    value="intern"
                    checked={adviserSide === 'intern'}
                    onChange={() => {
                      setAdviserSide('intern')
                    }}
                  />
                  <span>{T.binnen[language]}</span>
                </label>
                {adviceOffer?.buitenNwo === true ? (
                  <label className="ef-keuze">
                    <input
                      type="radio"
                      name={`${formId}-kant`}
                      value="extern"
                      checked={adviserSide === 'extern'}
                      onChange={() => {
                        setAdviserSide('extern')
                      }}
                    />
                    <span>{T.buiten[language]}</span>
                  </label>
                ) : null}
              </div>
              <p id={ids.kant} className="ef-veld__hulptekst">
                {adviserSide === 'intern' ? T.binnenUitleg[language] : T.buitenUitleg[language]}
              </p>
              {alertFor('adviseurKant')}
            </fieldset>
            <details
              className="ef-deeldialoog__deelt"
              open={sharedOpen}
              onToggle={(event) => {
                setSharedOpen((event.target as HTMLDetailsElement).open)
              }}
            >
              <summary className="ef-deeldialoog__deeltkop">
                {T.deelt[language].replace('{n}', sharedCount === 1 ? T.eenSectie[language] : T.nSecties[language].replace('{n}', String(sharedCount)))}
              </summary>
              <div ref={sectionsRef}>
                <SectionPicker
                  taal={language}
                  idBase={ids.secties}
                  legend={T.welkeDelen[language]}
                  mode="advies"
                  secties={sections}
                  value={shared}
                  onChange={setShared}
                  {...(pickerError === undefined ? {} : { fout: pickerError })}
                />
              </div>
            </details>
            <div className="ef-veld">
              <label htmlFor={ids.termijn} className="ef-veld__label">
                <span className="ef-veld__labeltekst">{T.termijn[language]}</span> <span className="ef-veld__hulptekst">{T.nietVerplicht[language]}</span>
              </label>
              <input
                id={ids.termijn}
                type="date"
                className="ef-invoer ef-deeldialoog__datum"
                value={deadline}
                onChange={(event) => {
                  setDeadline(event.target.value)
                }}
              />
            </div>
          </>
        ) : (
          <>
            {emailBlock}
            <SectionScope
              taal={language}
              idBase={formId}
              secties={sections}
              value={scope}
              onChange={(next) => {
                setScope(next)
                setError(undefined)
              }}
              notes={allNotes}
            />
            {scope === 'bepaald' ? (
              <div ref={sectionsRef} className="ef-reikwijdte__lijst">
                <SectionPicker
                  taal={language}
                  idBase={ids.secties}
                  legend={shownCount === 0 ? T.secties[language] : T.kies[language]}
                  legendVoorlezen={shownCount > 0}
                  mode={role === 'meeschrijven' ? 'schrijven' : 'lezen'}
                  secties={sections}
                  value={rights}
                  onChange={setRights}
                  {...(pickerError === undefined ? {} : { fout: pickerError })}
                />
              </div>
            ) : null}
          </>
        )}
        {error !== undefined && error.veld === undefined ? (
          <p id={ids.fout} role="alert" className="ef-veld__melding--hard">
            {error.tekst}
          </p>
        ) : null}
        <p className="ef-deeldialoog__samenvatting" data-testid="samenvatting">
          {summary}
        </p>
      </fieldset>
    </form>
  )
}
